// Calendar Persistence Service
import { getSupabaseUrl, getSupabaseAnonKey } from '../config/supabase';
import { queuedSupabaseOperation, getSupabaseAuthHeaders } from '../utils/supabaseWrapper';

/**
 * Get Supabase configuration for direct API calls
 */
const getSupabaseConfig = () => {
  const supabaseUrl = getSupabaseUrl();
  const supabaseKey = getSupabaseAnonKey();
  
  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Supabase configuration not found');
  }
  
  return { supabaseUrl, supabaseKey };
};

/**
 * Load active (non-archived) calendar weeks only
 */
export const loadCalendarWeeks = async (isAdmin = false) => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    const response = await fetch(`${supabaseUrl}/rest/v1/calendar_weeks?is_archived=eq.false&select=*&order=created_at.asc`, {
      method: 'GET',
      headers: await getSupabaseAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let weeks = await response.json();
    console.log('✅ Active calendar weeks loaded from Supabase:', weeks.length, 'weeks (before filtering)');
    
    // Filter out hidden weeks for non-admin users
    if (!isAdmin) {
      const beforeCount = weeks.length;
      weeks = weeks.filter(week => !week.is_hidden);
      const afterCount = weeks.length;
      const hiddenCount = beforeCount - afterCount;
      console.log(`🔒 Filtered out ${hiddenCount} hidden active weeks for non-admin user. Showing ${afterCount} weeks.`);
    } else {
      console.log('👑 Admin user - showing all active weeks including hidden ones');
    }
    
    // Also save to localStorage as backup
    if (weeks.length > 0) {
      localStorage.setItem('calendar_weeks', JSON.stringify(weeks));
    }
    
    return weeks;
  } catch (error) {
    console.error('❌ Error loading calendar weeks:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('calendar_weeks');
    const fallbackWeeks = saved ? JSON.parse(saved) : [];
    console.log('🔄 Fallback to localStorage:', fallbackWeeks.length, 'weeks');
    return fallbackWeeks;
  }
};

/**
 * Save a calendar week
 */
export const saveCalendarWeek = async (week) => {
  console.log('🔍 saveCalendarWeek called with:', week);
  
  try {
    const weekData = {
      week_id: week.id,
      title: week.title,
      start_date: week.start_date || week.startDate,
      end_date: week.end_date || week.endDate,
      dates: week.dates,
      year: week.year,
      week_number: week.weekNumber,
      is_archived: week.isArchived || false,
      is_hidden: week.isHidden || false,
      updated_at: new Date().toISOString()
    };

    console.log('📋 Prepared week data for Supabase:', weekData);

    // First try to update existing record
    try {
      console.log('🔄 Attempting UPDATE for week:', week.id);
      const result = await queuedSupabaseOperation.update(
        'calendar_weeks',
        weekData,
        `week_id=eq.${encodeURIComponent(week.id)}`,
        null, // No localStorage fallback for update
        `Update calendar week: ${week.id}`
      );
      console.log('✅ UPDATE result:', result);
      
      // Check if any rows were actually updated
      if (result && result.length > 0) {
        console.log('✅ Calendar week updated in Supabase:', week.id);
        return { data: result, error: null };
      } else {
        console.log('🔄 No rows updated, record doesn\'t exist. Trying INSERT...');
        throw new Error('No rows updated - record does not exist');
      }
    } catch (updateError) {
      // If update fails (record doesn't exist), try insert
      console.log('🔄 Update failed, trying insert for:', week.id, 'Error:', updateError.message);
      
      const insertData = {
        ...weekData,
        created_at: new Date().toISOString()
      };
      
      console.log('📝 Attempting INSERT with data:', insertData);
      
      try {
        const result = await queuedSupabaseOperation.insert(
          'calendar_weeks',
          insertData,
          'calendar_weeks', // localStorage fallback key
          `Insert calendar week: ${week.id}`
        );
        console.log('✅ INSERT result:', result);
        console.log('✅ Calendar week inserted in Supabase:', week.id);
        return { data: result, error: null };
      } catch (insertError) {
        // Check if this is a duplicate key error (409 conflict)
        const errorMessage = insertError.message || '';
        const isDuplicateKey = errorMessage.includes('duplicate key value violates unique constraint') ||
                              errorMessage.includes('23505') ||
                              errorMessage.includes('HTTP 409');
        
        if (isDuplicateKey) {
          console.log('📋 Calendar week already exists (duplicate key), treating as success:', week.id);
          return { data: [], error: null }; // Treat duplicate as success
        }
        // If it's a different error, re-throw it
        throw insertError;
      }
    }
  } catch (error) {
    console.error('❌ Error saving calendar week:', error);
    console.error('❌ Error details:', error.message, error.stack);
    return { data: null, error };
  }
};

/**
 * Check if a calendar week exists in Supabase
 */
export const checkCalendarWeekExists = async (weekId) => {
  try {
    console.log('🔍 Checking if calendar week exists:', weekId);
    
    const result = await queuedSupabaseOperation.select(
      'calendar_weeks',
      'week_id',
      `week_id=eq.${encodeURIComponent(weekId)}`,
      null, // No localStorage fallback for existence check
      `Check calendar week exists: ${weekId}`
    );
    
    const exists = result && result.length > 0;
    console.log('📋 Calendar week exists check result:', { weekId, exists, result });
    
    return exists;
  } catch (error) {
    console.error('❌ Error checking calendar week existence:', error);
    // If we can't check, assume it doesn't exist to allow creation
    return false;
  }
};

/**
 * Delete a calendar week
 */
export const deleteCalendarWeek = async (weekId) => {
  try {
    console.log('🗑️ Attempting to delete calendar week from Supabase:', weekId);
    
    const result = await queuedSupabaseOperation.delete(
      'calendar_weeks',
      `week_id=eq.${encodeURIComponent(weekId)}`,
      null, // No localStorage fallback for delete
      `Delete calendar week: ${weekId}`
    );
    
    console.log('🔍 Delete calendar week result:', result);
    
    if (result && result.error) {
      console.error('❌ Error deleting calendar week from Supabase:', result.error);
      return { error: result.error };
    }
    
    console.log('✅ Calendar week deleted from Supabase:', weekId);
    return { data: result, error: null };
  } catch (error) {
    console.error('❌ Exception deleting calendar week:', error);
    return { data: null, error };
  }
};

/**
 * Load all scheduled movies
 */
export const loadScheduledMovies = async (includeArchived = false) => {
  try {
    // Filter by archived status unless specifically requested
    const archivedFilter = includeArchived ? '' : '&is_archived=eq.false';
    const selectQuery = `select=*${archivedFilter}&order=date.asc,time_slot->>time.asc`;
    
    console.log('🔍 Loading scheduled movies with query:', selectQuery);
    console.log('🔍 includeArchived:', includeArchived, 'archivedFilter:', archivedFilter);
    
    const movies = await queuedSupabaseOperation.select(
      'scheduled_movies',
      selectQuery,
      'scheduled_movies', // localStorage fallback key
      `Load scheduled movies ${includeArchived ? '(including archived)' : '(active only)'}`
    );
    console.log('✅ Scheduled movies loaded from Supabase:', movies.length, 'movies', includeArchived ? '(including archived)' : '(active only)');
    
    if (includeArchived && movies.length > 0) {
      console.log('🎬 Sample archived movies:', movies.slice(0, 3));
      const archivedCount = movies.filter(m => m.is_archived === true).length;
      const activeCount = movies.filter(m => m.is_archived === false || m.is_archived === null).length;
      console.log('📊 Breakdown: archived:', archivedCount, 'active:', activeCount);
    }
    
    // Convert to the format expected by the app (grouped by date)
    const scheduledByDate = {};
    movies.forEach(movie => {
      const dateStr = movie.date;
      if (!scheduledByDate[dateStr]) {
        scheduledByDate[dateStr] = [];
      }
      
      scheduledByDate[dateStr].push({
        id: movie.schedule_id,
        movie: movie.movie_data,
        date: movie.date,
        timeSlot: movie.time_slot,
        slotsSpanned: movie.slots_spanned,
        isArchived: movie.is_archived || false
      });
    });
    
    // Also save to localStorage as backup
    if (Object.keys(scheduledByDate).length > 0) {
      localStorage.setItem('scheduled_movies', JSON.stringify(scheduledByDate));
    }
    
    return scheduledByDate;
  } catch (error) {
    console.error('❌ Error loading scheduled movies:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('scheduled_movies');
    const fallbackMovies = saved ? JSON.parse(saved) : {};
    console.log('🔄 Fallback to localStorage:', Object.keys(fallbackMovies).length, 'dates');
    return fallbackMovies;
  }
};

/**
 * Save scheduled movies for a specific date
 */
export const saveScheduledMoviesForDate = async (dateStr, movies) => {
  try {
    // First, delete existing movies for this date using queue system
    await queuedSupabaseOperation.delete(
      'scheduled_movies',
      `date=eq.${dateStr}`,
      null, // No localStorage fallback for delete
      `Clear scheduled movies for ${dateStr}`
    );

    // Then insert new movies if any
    if (movies.length > 0) {
      const movieData = movies.map(movie => ({
        schedule_id: movie.id,
        movie_id: movie.movie.id,
        movie_data: movie.movie,
        date: dateStr,
        time_slot: movie.timeSlot,
        slots_spanned: movie.slotsSpanned || 1,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));

      await queuedSupabaseOperation.insert(
        'scheduled_movies',
        movieData,
        `scheduled_movies_${dateStr}`, // localStorage fallback key
        `Save ${movies.length} scheduled movies for ${dateStr}`
      );
    }

    console.log('✅ Scheduled movies saved for date:', dateStr, movies.length, 'movies');
    return { error: null };
  } catch (error) {
    console.error('❌ Error saving scheduled movies:', error);
    return { error };
  }
};

/**
 * Archive scheduled movies for a week (instead of deleting)
 */
export const archiveScheduledMoviesForWeek = async (dates) => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    // First, let's check what movies exist before archiving
    console.log('🔍 Checking existing movies before archiving...');
    const checkResponse = await fetch(`${supabaseUrl}/rest/v1/scheduled_movies?select=*`, {
      method: 'GET',
      headers: await getSupabaseAuthHeaders()
    });
    
    if (checkResponse.ok) {
      const allMovies = await checkResponse.json();
      console.log('📊 Total movies in database before archiving:', allMovies.length);
      if (allMovies.length > 0) {
        console.log('🎬 Sample movies:', allMovies.slice(0, 3));
      }
    }
    
    // Archive movies for each date in the week
    for (const date of dates) {
      // Convert Date object to YYYY-MM-DD string format
      const dateStr = date.toISOString().split('T')[0];
      console.log('🔍 Archiving movies for date:', dateStr);
      const response = await fetch(`${supabaseUrl}/rest/v1/scheduled_movies?date=eq.${dateStr}`, {
        method: 'PATCH',
        headers: await getSupabaseAuthHeaders({ 'Prefer': 'return=minimal' }),
        body: JSON.stringify({
          is_archived: true,
          updated_at: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ Failed to archive scheduled movies for ${dateStr}:`, response.status, errorText);
      } else {
        const responseText = await response.text();
        console.log(`✅ Successfully archived movies for ${dateStr}. Response:`, responseText);
      }
    }
    
    console.log('✅ Scheduled movies archived for week:', dates);
    return { error: null };
  } catch (error) {
    console.error('❌ Error archiving scheduled movies for week:', error);
    return { error };
  }
};

/**
 * Delete all scheduled movies for a calendar week
 */
export const deleteScheduledMoviesForWeek = async (dates) => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    // Delete movies for all dates in the week
    for (const date of dates) {
      await fetch(`${supabaseUrl}/rest/v1/scheduled_movies?date=eq.${date}`, {
        method: 'DELETE',
        headers: await getSupabaseAuthHeaders()
      });
    }

    console.log('✅ Scheduled movies deleted for week dates:', dates);
    return { error: null };
  } catch (error) {
    console.error('❌ Error deleting scheduled movies for week:', error);
    return { error };
  }
};

/**
 * Load ALL calendar weeks (including archived) - for admin purposes
 */
export const loadAllCalendarWeeks = async () => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    const response = await fetch(`${supabaseUrl}/rest/v1/calendar_weeks?select=*&order=created_at.asc`, {
      method: 'GET',
      headers: await getSupabaseAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const weeks = await response.json();
    console.log('✅ ALL calendar weeks loaded from Supabase:', weeks.length, 'weeks (including archived)');
    
    return weeks;
  } catch (error) {
    console.error('❌ Error loading all calendar weeks:', error);
    return [];
  }
};

/**
 * Load archived calendar weeks
 */
export const loadArchivedCalendarWeeks = async (isAdmin = false) => {
  try {
    const { supabaseUrl } = getSupabaseConfig();
    
    const response = await fetch(`${supabaseUrl}/rest/v1/calendar_weeks?is_archived=eq.true&select=*&order=year.desc,start_date.asc`, {
      method: 'GET',
      headers: await getSupabaseAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let archivedWeeks = await response.json();
    console.log('✅ Archived calendar weeks loaded from Supabase:', archivedWeeks.length, 'weeks (before filtering)');
    
    // Filter out hidden weeks for non-admin users
    if (!isAdmin) {
      const beforeCount = archivedWeeks.length;
      archivedWeeks = archivedWeeks.filter(week => !week.is_hidden);
      const afterCount = archivedWeeks.length;
      const hiddenCount = beforeCount - afterCount;
      console.log(`🔒 Filtered out ${hiddenCount} hidden weeks for non-admin user. Showing ${afterCount} weeks.`);
    } else {
      console.log('👑 Admin user - showing all archived weeks including hidden ones');
    }
    
    // Debug: Log the first few archived weeks to understand the data structure
    if (archivedWeeks.length > 0) {
      console.log('🔍 Debug: First archived week data:', archivedWeeks[0]);
      if (archivedWeeks[0].dates) {
        console.log('🔍 Debug: First week dates:', archivedWeeks[0].dates);
        archivedWeeks[0].dates.forEach((dateStr, index) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          const dateObj = new Date(year, month - 1, day);
          const dayOfWeek = dateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek];
          console.log(`🔍 Debug: ${dateStr} (index ${index}) = ${dayName} (${dayOfWeek})`);
        });
      }
    }
    
    // Transform the data to match the expected format for ArchiveCalendar component
    const transformedWeeks = archivedWeeks.map(week => {
      // Reconstruct the week to ensure proper Sunday-Saturday alignment
      const originalDates = week.dates.map(dateStr => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      });
      
      // Find the first date and determine what day of week it should be
      const firstDate = originalDates[0];
      const firstDayOfWeek = firstDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      
      // Create a proper week starting with Sunday
      const reconstructedWeek = [];
      const startOfWeek = new Date(firstDate);
      startOfWeek.setDate(firstDate.getDate() - firstDayOfWeek); // Go back to Sunday
      
      // Build 7-day week starting from Sunday
      for (let i = 0; i < 7; i++) {
        const weekDate = new Date(startOfWeek);
        weekDate.setDate(startOfWeek.getDate() + i);
        reconstructedWeek.push(weekDate);
      }
      
      console.log(`🔧 Reconstructed week ${week.week_id}:`);
      console.log(`   Original first date: ${firstDate.toDateString()} (day ${firstDayOfWeek})`);
      console.log(`   Reconstructed week: ${reconstructedWeek.map(d => d.toDateString()).join(', ')}`);
      
      return {
        id: week.week_id,
        title: week.title,
        week: reconstructedWeek,
        archivedDate: new Date(week.updated_at),
        year: week.year,
        dates: week.dates, // Keep original dates for compatibility
        start_date: week.start_date,
        end_date: week.end_date,
        isArchived: week.is_archived,
        isHidden: week.is_hidden,
        // Add flag to indicate this week has been reconstructed
        isReconstructed: true
      };
    });
    
    return transformedWeeks;
  } catch (error) {
    console.error('❌ Error loading archived calendar weeks:', error);
    // Fallback to localStorage
    const saved = localStorage.getItem('archivedWeeks');
    const fallbackWeeks = saved ? JSON.parse(saved) : [];
    console.log('🔄 Fallback to localStorage:', fallbackWeeks.length, 'archived weeks');
    return fallbackWeeks;
  }
};

/**
 * Get available archive years from archived weeks
 */
export const getAvailableArchiveYears = async () => {
  try {
    const archivedWeeks = await loadArchivedCalendarWeeks();
    const years = new Set();
    
    archivedWeeks.forEach(week => {
      if (week.year) {
        years.add(week.year.toString());
      } else if (week.start_date) {
        const year = new Date(week.start_date).getFullYear();
        years.add(year.toString());
      }
    });
    
    return Array.from(years).sort((a, b) => b.localeCompare(a)); // Most recent first
  } catch (error) {
    console.error('❌ Error getting available archive years:', error);
    return [];
  }
};

/**
 * Get archived weeks for a specific year
 */
export const getArchivedWeeksForYear = async (year) => {
  try {
    const archivedWeeks = await loadArchivedCalendarWeeks();
    
    return archivedWeeks.filter(week => {
      if (week.year) {
        return week.year.toString() === year;
      } else if (week.start_date) {
        const weekYear = new Date(week.start_date).getFullYear();
        return weekYear.toString() === year;
      }
      return false;
    }).sort((a, b) => {
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      return dateA.getTime() - dateB.getTime();
    });
  } catch (error) {
    console.error('❌ Error getting archived weeks for year:', error);
    return [];
  }
};

